import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RagBadge } from "@/components/common/RagBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResourceFormDialog, AckReportDialog } from "@/components/workspace/ResourceDialogs";
import { openFile } from "@/lib/workspace";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const KIND = { document: ["FileText", "Document"], link: ["ExternalLink", "Link"], article: ["Newspaper", "Article"] };
const AUDIENCE = { all: "Everyone", roles: "Selected roles", departments: "Selected departments", users: "Named people" };

export function ResourceList({ orgId, resources, canAdmin, canManage, onChanged }) {
  const [reading, setReading] = useState(null);
  const [editing, setEditing] = useState(null); // {mode:'create'|'version', res}
  const [report, setReport] = useState(null);
  const ack = async (r) => {
    try { await api.post(`/workspace/resources/${r.id}/acknowledge`); toast.success("Thanks — acknowledged"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const open = (r) => {
    if (r.kind === "article") return setReading(r);
    if (r.kind === "link") return window.open(r.url, "_blank", "noopener,noreferrer");
    if (r.file) openFile(r.file).catch(() => toast.error("You can't open this file."));
  };
  return (
    <div data-testid="resource-list">
      {canAdmin && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setEditing({ mode: "create" })} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-resource-btn">
            <Icons.Plus size={16} className="mr-1.5" /> Publish resource
          </Button>
        </div>
      )}
      {resources.length === 0 ? (
        <EmptyState icon="BookOpen" title="No company resources yet" description="Policies, guides and links shared with you will appear here." testId="resources-empty" />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {resources.map((r) => {
            const [icon, label] = KIND[r.kind] || KIND.document;
            const I = Icons[icon];
            const needsAck = r.requires_acknowledgement && r.in_my_audience && !r.acknowledged;
            return (
              <div key={r.id} className="amden-card flex flex-col p-5" data-testid={`resource-${r.id}`}>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0B1729] text-[#FFC24D]"><I size={18} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-semibold text-[#0B1729]">{r.title}</div>
                    <div className="mt-0.5 text-xs text-[#1E2D44]/50">{label} · version {r.version} · {fmtDate(r.updated_at)}{canAdmin ? ` · ${AUDIENCE[r.audience_type]}` : ""}</div>
                  </div>
                  {needsAck && <RagBadge rag="amber" testId={`ack-needed-${r.id}`}>To acknowledge</RagBadge>}
                  {r.requires_acknowledgement && r.acknowledged && <RagBadge rag="green">Acknowledged</RagBadge>}
                </div>
                {r.summary && <p className="mt-3 text-sm text-[#1E2D44]/70">{r.summary}</p>}
                <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                  <Button size="sm" variant="outline" onClick={() => open(r)} data-testid={`open-resource-${r.id}`}>Open</Button>
                  {needsAck && <Button size="sm" className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" onClick={() => ack(r)} data-testid={`ack-resource-${r.id}`}>I've read this</Button>}
                  {canManage && r.ack_summary && (
                    <button className="text-xs font-medium text-[#B87F1E] hover:underline" onClick={() => setReport(r)} data-testid={`ack-report-${r.id}`}>
                      {r.ack_summary.acknowledged}/{r.ack_summary.expected} acknowledged
                    </button>
                  )}
                  {canAdmin && <button className="ml-auto text-xs text-[#1E2D44]/55 hover:text-[#0B1729]" onClick={() => setEditing({ mode: "version", res: r })} data-testid={`new-version-${r.id}`}>New version</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {reading && (
        <Dialog open onOpenChange={() => setReading(null)}>
          <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="article-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{reading.title}</DialogTitle>
              <DialogDescription>Version {reading.version} · updated {fmtDate(reading.updated_at)}</DialogDescription>
            </DialogHeader>
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-[#1E2D44]/85">{reading.body}</div>
            {reading.requires_acknowledgement && reading.in_my_audience && !reading.acknowledged && (
              <Button className="mt-2 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" onClick={() => { ack(reading); setReading(null); }} data-testid="ack-article-btn">I've read and understood this</Button>
            )}
          </DialogContent>
        </Dialog>
      )}
      {editing && <ResourceFormDialog orgId={orgId} mode={editing.mode} res={editing.res} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      {report && <AckReportDialog res={report} onClose={() => setReport(null)} />}
    </div>
  );
}
